/**
 * Hook for managing PolicyRule TargetSet and RequestorSet filter scopes
 * 
 * This hook handles:
 * 1. Fetching and caching Set entities
 * 2. Extracting FilterScope from Sets
 * 3. Managing set change handlers
 * 4. Error handling and retry prevention
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ScimEntityService } from '../services/ScimEntityService';
import { extractReferenceId, extractReferenceDisplayName } from '../utils/referenceUtils';

export interface SetCacheEntry {
    entity: any;
    filterScope: string | null;
}

export interface UsePolicyRuleSetScopesOptions {
    isPolicyRule: boolean;
    open: boolean;
    targetSetId: string | null;
    requestorSetId: string | null;
    entityService: ScimEntityService;
}

export interface UsePolicyRuleSetScopesResult {
    targetSetFilterScope: string | null;
    requestorSetFilterScope: string | null;
    setEntitiesCache: React.MutableRefObject<Map<string, SetCacheEntry>>;
    handleTargetSetChange: (setId: string | null) => void;
    handleRequestorSetChange: (setId: string | null) => void;
    fetchTargetSetFilterScope: (setId: string) => Promise<void>;
    fetchRequestorSetFilterScope: (setId: string) => Promise<void>;
}

export function usePolicyRuleSetScopes(
    options: UsePolicyRuleSetScopesOptions
): UsePolicyRuleSetScopesResult {
    const {
        isPolicyRule,
        open,
        targetSetId,
        requestorSetId,
        entityService
    } = options;

    // State for filter scopes
    const [targetSetFilterScope, setTargetSetFilterScope] = useState<string | null>(null);
    const [requestorSetFilterScope, setRequestorSetFilterScope] = useState<string | null>(null);

    // Cache for Set entities to avoid refetching
    const setEntitiesCacheRef = useRef<Map<string, SetCacheEntry>>(new Map());
    const failedSetsRef = useRef<Set<string>>(new Set());

    // Fetch TargetSet FilterScope (with caching)
    const fetchTargetSetFilterScope = useCallback(async (setId: string) => {
        if (failedSetsRef.current.has(setId)) {
            return;
        }

        // Check cache first
        const cached = setEntitiesCacheRef.current.get(setId);
        if (cached) {
            setTargetSetFilterScope(cached.filterScope);
            return;
        }

        try {
            const set = await entityService.getEntityById(setId, 'Set', true);
            const filterScopeValue = set.FilterScope || set.filterScope;
            // Extract ID from filterScope (handles both stale references as strings and expanded reference objects)
            const filterScope = extractReferenceId(filterScopeValue) || extractReferenceDisplayName(filterScopeValue);

            // Cache the Set entity and filter scope
            setEntitiesCacheRef.current.set(setId, { entity: set, filterScope });
            setTargetSetFilterScope(filterScope);
        } catch (error: any) {
            const statusCode = error?.response?.status || error?.status;
            const isNotFound = statusCode === 404 || error?.message?.toLowerCase().includes('not found');
            if (isNotFound) {
                failedSetsRef.current.add(setId);
            }
            setTargetSetFilterScope(null);
        }
    }, [entityService]);

    // Fetch RequestorSet FilterScope (with caching)
    const fetchRequestorSetFilterScope = useCallback(async (setId: string) => {
        if (failedSetsRef.current.has(setId)) {
            return;
        }

        // Check cache first
        const cached = setEntitiesCacheRef.current.get(setId);
        if (cached) {
            setRequestorSetFilterScope(cached.filterScope);
            return;
        }

        try {
            const set = await entityService.getEntityById(setId, 'Set', true);
            const filterScopeValue = set.FilterScope || set.filterScope;
            // Extract ID from filterScope (handles both stale references as strings and expanded reference objects)
            const filterScope = extractReferenceId(filterScopeValue) || extractReferenceDisplayName(filterScopeValue);

            // Cache the Set entity and filter scope
            setEntitiesCacheRef.current.set(setId, { entity: set, filterScope });
            setRequestorSetFilterScope(filterScope);
        } catch (error: any) {
            const statusCode = error?.response?.status || error?.status;
            const isNotFound = statusCode === 404 || error?.message?.toLowerCase().includes('not found');
            if (isNotFound) {
                failedSetsRef.current.add(setId);
            }
            setRequestorSetFilterScope(null);
        }
    }, [entityService]);

    // Handle TargetSet change
    const handleTargetSetChange = useCallback((setId: string | null) => {
        if (!setId) {
            setTargetSetFilterScope(null);
            return;
        }

        // Check cache first
        const cached = setEntitiesCacheRef.current.get(setId);
        if (cached) {
            setTargetSetFilterScope(cached.filterScope);
        } else {
            // Fetch and cache
            fetchTargetSetFilterScope(setId);
        }
    }, [fetchTargetSetFilterScope]);

    // Handle RequestorSet change
    const handleRequestorSetChange = useCallback((setId: string | null) => {
        if (!setId) {
            setRequestorSetFilterScope(null);
            return;
        }

        // Check cache first
        const cached = setEntitiesCacheRef.current.get(setId);
        if (cached) {
            setRequestorSetFilterScope(cached.filterScope);
        } else {
            // Fetch and cache
            fetchRequestorSetFilterScope(setId);
        }
    }, [fetchRequestorSetFilterScope]);

    // Load initial filter scopes when dialog opens with existing values
    useEffect(() => {
        if (!isPolicyRule || !open) {
            return;
        }

        // Only fetch if we don't already have the filter scope cached
        if (targetSetId) {
            const cached = setEntitiesCacheRef.current.get(targetSetId);
            if (cached) {
                setTargetSetFilterScope(cached.filterScope);
            } else {
                fetchTargetSetFilterScope(targetSetId);
            }
        }

        if (requestorSetId) {
            const cached = setEntitiesCacheRef.current.get(requestorSetId);
            if (cached) {
                setRequestorSetFilterScope(cached.filterScope);
            } else {
                fetchRequestorSetFilterScope(requestorSetId);
            }
        }
    }, [isPolicyRule, open, targetSetId, requestorSetId, fetchTargetSetFilterScope, fetchRequestorSetFilterScope]);

    return {
        targetSetFilterScope,
        requestorSetFilterScope,
        setEntitiesCache: setEntitiesCacheRef,
        handleTargetSetChange,
        handleRequestorSetChange,
        fetchTargetSetFilterScope,
        fetchRequestorSetFilterScope
    };
}
